import type { Task } from "@app/core";
import { create } from "zustand";

interface DialogState {
  open: boolean;
  /** When set, dialog opens in edit mode for this task. Null = create mode. */
  editing: Task | null;
  /** When set, dialog opens in create mode with this date prefilled. */
  defaultDate: Date | null;
  /** When set, dialog prefills duration (used by drag-to-create on calendar). */
  defaultDuration: number | null;
  /** Override the new-entry is_event default (the inbox creates plain tasks). */
  defaultIsEvent: boolean | null;
  /** Prefilled title (the mobile quick-create sheet expanding to the full form). */
  defaultTitle: string | null;
  openCreate: (defaultDate?: Date, defaultDuration?: number, defaultIsEvent?: boolean, defaultTitle?: string) => void;
  openEdit: (task: Task) => void;
  close: () => void;
}

const CLEARED = { editing: null, defaultDate: null, defaultDuration: null, defaultIsEvent: null, defaultTitle: null };

/**
 * Cross-route task dialog state. The Shell renders <TaskFormDialog/> once;
 * any view triggers it by calling these actions.
 */
export const useTaskDialogStore = create<DialogState>((set, get) => ({
  open: false,
  ...CLEARED,
  openCreate: (defaultDate, defaultDuration, defaultIsEvent, defaultTitle) => {
    // Ignore re-triggers while a dialog is already open — e.g. a calendar tap
    // reaching the grid behind the mobile create sheet (the scrim passes through
    // so the on-grid draft ghost stays draggable) must not reset the form.
    if (get().open) return;
    set({
      open: true,
      editing: null,
      defaultDate: defaultDate ?? null,
      defaultDuration: defaultDuration ?? null,
      defaultIsEvent: defaultIsEvent ?? null,
      defaultTitle: defaultTitle ?? null,
    });
  },
  openEdit: (task) => { if (get().open) return; set({ open: true, ...CLEARED, editing: task }); },
  close: () => set({ open: false, ...CLEARED }),
}));
